import { useState, useEffect } from 'react';
import { 
  Link, Star, MessageSquare, Layers, Sparkles, 
  MousePointer, Image, Wand2, ArrowRight, Check, Menu, X,
  Play, Zap, Shield, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { publicSiteApi } from '@/lib/api';

// Boxit Logo Component - Premium modern font
const BoxitLogo = ({ light = false, size = "default" }) => (
  <div 
    className={cn(
      "tracking-tight select-none",
      size === "large" ? "text-4xl" : "text-2xl",
      light ? "text-white" : "text-slate-900"
    )}
    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' }}
  >
    <span>BOX</span>
    <span className="text-orange-500">IT</span>
  </div>
);

// Navigation Component
const Navigation = ({ scrolled, onLoginClick, onGetStartedClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Custom smooth scroll with slower, eased animation
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    
    setMobileOpen(false);
    
    const targetPosition = element.getBoundingClientRect().top + window.scrollY;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    const duration = 1200; // 1.2 seconds for smoother feel
    let startTime = null;

    // Ease-out cubic for smooth deceleration
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = easeOutCubic(progress);
      
      window.scrollTo(0, startPosition + distance * easeProgress);
      
      if (elapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
      scrolled ? "bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-sm" : "bg-transparent"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="cursor-pointer" onClick={() => scrollToSection('hero')}>
            <BoxitLogo light={!scrolled} />
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {['packages', 'examples', 'how-it-works'].map((item) => (
              <button 
                key={item}
                onClick={() => scrollToSection(item)} 
                className={cn(
                  "text-sm font-semibold uppercase tracking-wider transition-all hover:tracking-widest",
                  scrolled ? "text-slate-500 hover:text-orange-500" : "text-white/80 hover:text-white"
                )}
              >
                {item.replace('-', ' ')}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-6">
            <button 
              onClick={onLoginClick}
              className={cn(
                "text-sm font-semibold transition-colors",
                scrolled ? "text-slate-600 hover:text-orange-500" : "text-white/90 hover:text-white"
              )}
              data-testid="nav-login-btn"
            >
              Login
            </button>
            <Button 
              onClick={onGetStartedClick}
              className="bg-emerald-500 text-white hover:bg-emerald-400 rounded-full px-7 py-5 text-sm font-bold transition-all hover:scale-105 hover:shadow-xl hover:shadow-emerald-500/25"
              data-testid="nav-get-started-btn"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 rounded-full transition-transform duration-200 active:scale-90"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6">
              <X className={cn(
                "w-6 h-6 absolute inset-0 transition-all duration-300 ease-out",
                mobileOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90",
                scrolled ? "text-slate-900" : "text-white"
              )} />
              <Menu className={cn(
                "w-6 h-6 absolute inset-0 transition-all duration-300 ease-out",
                mobileOpen ? "opacity-0 -rotate-90" : "opacity-100 rotate-0",
                scrolled ? "text-slate-900" : "text-white"
              )} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu - Smooth animated panel */}
      <div 
        className={cn(
          "md:hidden overflow-hidden transition-all duration-300 ease-out",
          mobileOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="bg-white/95 backdrop-blur-xl border-t border-slate-100 py-6 px-6 space-y-1">
          {['packages', 'examples', 'how-it-works'].map((item, idx) => (
            <button 
              key={item}
              onClick={() => scrollToSection(item)} 
              className={cn(
                "block w-full text-left py-3 text-slate-700 font-semibold uppercase tracking-wider",
                "transition-all duration-300 ease-out hover:text-orange-500 hover:translate-x-2",
              )}
              style={{ 
                transitionDelay: mobileOpen ? `${idx * 50}ms` : '0ms',
                opacity: mobileOpen ? 1 : 0,
                transform: mobileOpen ? 'translateX(0)' : 'translateX(-10px)'
              }}
            >
              {item.replace('-', ' ')}
            </button>
          ))}
          <hr className="border-slate-200 my-4" />
          <button 
            onClick={onLoginClick} 
            className="block w-full text-left py-3 text-slate-600 font-semibold transition-colors hover:text-orange-500"
            style={{ 
              transitionDelay: mobileOpen ? '150ms' : '0ms',
              opacity: mobileOpen ? 1 : 0 
            }}
          >
            Login
          </button>
          <Button 
            onClick={onGetStartedClick} 
            className="w-full bg-emerald-500 text-white rounded-full font-bold mt-2 transition-all hover:bg-emerald-400"
            style={{ 
              transitionDelay: mobileOpen ? '200ms' : '0ms',
              opacity: mobileOpen ? 1 : 0 
            }}
          >
            Get Started
          </Button>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onViewPackages, onSeeExamples }) => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background - Stage/Entertainment Image with Video overlay */}
      <div className="absolute inset-0 z-0">
        {/* Fallback image */}
        <img 
          src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=2000&q=80"
          alt="Concert stage"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Video overlay */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
        >
          <source src="https://assets.mixkit.co/videos/preview/mixkit-concert-crowd-dancing-and-cheering-4717-large.mp4" type="video/mp4" />
        </video>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-purple-900/70 to-slate-900/80" />
      </div>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 z-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-purple-500 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-orange-500 rounded-full blur-[120px] animate-pulse delay-1000" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center pt-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-5 py-2.5 mb-10">
          <Zap className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-semibold text-white/95 uppercase tracking-wider">For Entertainment Professionals</span>
        </div>

        {/* Main heading */}
        <h1 
          className="text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] text-white mb-6"
          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
        >
          Your digital showcase.
        </h1>
        <h2 
          className="text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[0.9] mb-10"
          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
        >
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500">
            The online business card for entertainment.
          </span>
        </h2>

        {/* Subheading */}
        <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-12 leading-relaxed">
          Professional showcase websites for performers, actors, musicians and creatives. Your career deserves centre stage.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
          <Button 
            onClick={onViewPackages}
            className="group bg-emerald-500 text-white hover:bg-emerald-400 rounded-full px-10 py-7 text-lg font-bold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/30"
            data-testid="hero-view-packages-btn"
          >
            View Packages
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button 
            onClick={onSeeExamples}
            variant="outline"
            className="bg-white/5 backdrop-blur-xl text-white border-white/20 hover:bg-white/10 hover:border-white/40 rounded-full px-10 py-7 text-lg font-semibold transition-all"
            data-testid="hero-see-examples-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            See Examples
          </Button>
        </div>

        {/* Trust indicators */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-white/50 text-sm">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>No contracts</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Live in days</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>AI-powered updates</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = ({ stats }) => {
  // Fallback data if API hasn't loaded yet
  const displayStats = stats.length > 0 ? stats : [
    { value: "500+", label: "Performers Online" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "48hr", label: "Average Build Time" },
    { value: "£12", label: "Starting From /mo" },
  ];

  return (
    <section className="py-16 bg-slate-900 border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {displayStats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <div 
                className="text-4xl md:text-5xl text-white mb-2"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
              >
                {stat.value}
              </div>
              <div className="text-sm text-slate-400 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Performers Need a Website Section
const WhyWebsiteSection = () => {
  const features = [
    { icon: Link, title: "One Link You Control", description: "Your professional home that you own. Not rented social space that can disappear." },
    { icon: Star, title: "First Impressions Matter", description: "Look polished and professional before clients even reach out to you." },
    { icon: MessageSquare, title: "Direct Enquiries", description: "No algorithms deciding who sees you. Clients reach you directly." },
    { icon: Layers, title: "Everything In One Place", description: "Bio, showreels, testimonials, booking info — all under one roof." },
  ];

  return (
    <section id="why-website" className="py-28 md:py-36 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-orange-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-70" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-20">
          <span className="inline-block text-sm font-bold uppercase tracking-widest text-orange-500 mb-4 px-4 py-2 bg-orange-50 rounded-full">Why a Website?</span>
          <h2 
            className="text-4xl md:text-6xl tracking-tight text-slate-900 mb-6"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
          >
            Social is for discovery.
            <br />
            <span className="text-orange-500">Boxit is for bookings.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className="group bg-white border border-slate-200 p-10 rounded-[2rem] hover:shadow-2xl hover:shadow-orange-500/10 hover:border-orange-200 transition-all duration-500"
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center flex-shrink-0 group-hover:from-orange-500 group-hover:to-orange-400 transition-all duration-500">
                  <feature.icon className="w-7 h-7 text-orange-500 group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-lg text-slate-500 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// AI-Powered Admin Section
const AIAdminSection = () => {
  const features = [
    { icon: MousePointer, title: "Drag & Drop", description: "Rearrange anything instantly" },
    { icon: Sparkles, title: "AI Writing", description: "Generate copy in seconds" },
    { icon: Image, title: "Media Swaps", description: "Update photos effortlessly" },
    { icon: Wand2, title: "Zero Code", description: "No tech skills required" },
  ];

  return (
    <section id="ai-admin" className="py-28 md:py-36 bg-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-orange-500 mb-4 px-4 py-2 bg-orange-100 rounded-full">Next-Gen Admin</span>
            <h2 
              className="text-4xl md:text-6xl tracking-tight text-slate-900 mb-8"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
            >
              Like having a 
              <span className="text-orange-500"> digital assistant</span>
            </h2>
            <p className="text-xl text-slate-500 mb-10 leading-relaxed">
              Update your website in minutes, not hours. Our AI-powered admin panel makes managing your online presence effortless.
            </p>

            <div className="grid grid-cols-2 gap-5">
              {features.map((feature, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-orange-200 hover:shadow-lg transition-all">
                  <feature.icon className="w-8 h-8 text-orange-500 mb-3" strokeWidth={1.5} />
                  <h4 className="font-bold text-slate-900 mb-1">{feature.title}</h4>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Admin Preview */}
          <div className="relative">
            <div className="bg-white rounded-[2rem] shadow-2xl shadow-slate-200/50 p-8 border border-slate-200">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <div className="w-3 h-3 rounded-full bg-emerald-400" />
                <div className="flex-1 ml-4 h-8 bg-slate-100 rounded-lg" />
              </div>
              
              {/* Content */}
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-100">
                  <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">AI is writing your bio...</div>
                    <div className="text-sm text-slate-500">Based on your profile</div>
                  </div>
                </div>
                
                <div className="bg-slate-100 rounded-xl p-5 border-2 border-dashed border-slate-300">
                  <div className="text-center text-slate-500">
                    <Image className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <span className="font-medium">Drop new photos here</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="aspect-square bg-gradient-to-br from-slate-200 to-slate-100 rounded-xl" />
                  ))}
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute -bottom-6 -right-6 bg-emerald-500 text-white rounded-2xl px-6 py-3 shadow-xl shadow-emerald-500/30 font-bold">
              Updates in seconds ⚡
            </div>
            <div className="absolute -top-4 -left-4 bg-white rounded-2xl px-5 py-3 shadow-xl border border-slate-200">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Packages Section
const PackagesSection = () => {
  const tiers = [
    {
      id: "starter",
      name: "Starter",
      tagline: "For solo performers",
      monthly: 12,
      annual: 120,
      setup: 0,
      is_popular: false,
      features: [
        "Custom designed homepage",
        "About & bio section",
        "Photo gallery",
        "Contact form with email forwarding",
        "Social media links",
        "Mobile responsive",
        "SSL secured hosting",
      ],
    },
    {
      id: "professional",
      name: "Professional",
      tagline: "For established performers & small companies",
      monthly: 22,
      annual: 220,
      setup: 99,
      is_popular: true,
      features: [
        "Everything in Starter",
        "Events & dates calendar",
        "Video gallery (YouTube, Vimeo)",
        "Music section (Spotify, SoundCloud, MP3)",
        "Press & reviews section",
        "Services listing",
        "FAQ section",
        "Mailing list signup",
      ],
    },
    {
      id: "business",
      name: "Business",
      tagline: "For agencies, producers & companies",
      monthly: 42,
      annual: 420,
      setup: 299,
      is_popular: false,
      features: [
        "Everything in Professional",
        "News & blog section",
        "Team members page",
        "Testimonials showcase",
        "All current & future modules",
        "Priority build queue",
        "Advanced analytics",
      ],
    },
  ];

  return (
    <section id="packages" className="py-28 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <span className="inline-block text-sm font-bold uppercase tracking-widest text-orange-500 mb-4 px-4 py-2 bg-orange-50 rounded-full">Simple Pricing</span>
          <h2 
            className="text-4xl md:text-6xl tracking-tight text-slate-900 mb-6"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
          >
            Packages for every stage.
          </h2>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto">
            No hidden fees. No contracts. <span className="text-orange-500 font-bold">Cancel anytime.</span>
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {tiers.map((tier) => (
            <div 
              key={tier.id}
              data-testid={`pricing-card-${tier.id}`}
              className={cn(
                "relative bg-white p-10 rounded-[2rem] transition-all duration-500",
                tier.is_popular 
                  ? "border-2 border-orange-500 shadow-2xl shadow-orange-500/20 scale-105 z-10" 
                  : "border border-slate-200 hover:border-orange-200 hover:shadow-xl"
              )}
            >
              {tier.is_popular && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-bold uppercase tracking-wider px-6 py-2 rounded-full shadow-lg">
                  Most Popular
                </div>
              )}

              <h3 
                className="text-3xl text-slate-900 mb-1"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
              >
                {tier.name}
              </h3>
              <p className="text-slate-500 mb-8">{tier.tagline}</p>

              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span 
                    className="text-6xl text-slate-900"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
                  >
                    £{tier.monthly}
                  </span>
                  <span className="text-slate-400 font-medium">/month</span>
                </div>
                {tier.setup > 0 ? (
                  <p className="text-sm text-slate-400 mt-2">+ £{tier.setup} one-time setup</p>
                ) : (
                  <p className="text-sm text-emerald-500 font-semibold mt-2">Free setup</p>
                )}
              </div>

              <ul className="space-y-4 mb-10">
                {tier.features.map((feature, fidx) => (
                  <li key={fidx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                onClick={() => window.location.href = `/order?package=${tier.id}`}
                className={cn(
                  "w-full rounded-full py-7 font-bold transition-all",
                  tier.is_popular 
                    ? "bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40" 
                    : "bg-slate-100 text-slate-900 hover:bg-orange-500 hover:text-white"
                )}
                data-testid={`package-${tier.id}-btn`}
              >
                {tier.setup === 0 ? 'Start Free' : 'Get Started'}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { number: "01", title: "Send Your Content", description: "Share photos, videos, bio and details about your act." },
    { number: "02", title: "We Build It", description: "Our team creates your stunning website in days." },
    { number: "03", title: "Review & Refine", description: "Request changes until it's perfect." },
    { number: "04", title: "Go Live", description: "Launch and start getting booked." },
  ];

  return (
    <section id="how-it-works" className="py-28 md:py-36 bg-slate-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-orange-500 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-emerald-500 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-20">
          <span className="inline-block text-sm font-bold uppercase tracking-widest text-orange-400 mb-4 px-4 py-2 bg-orange-500/20 rounded-full">The Process</span>
          <h2 
            className="text-4xl md:text-6xl tracking-tight mb-6"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
          >
            Live in days, not weeks
          </h2>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, idx) => (
            <div key={idx} className="relative text-center md:text-left">
              <div className="text-8xl font-black text-orange-500/20 mb-4 leading-none">{step.number}</div>
              <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
              <p className="text-slate-400 text-lg">{step.description}</p>
              
              {idx < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 right-0 translate-x-1/2 z-10">
                  <ArrowRight className="w-8 h-8 text-orange-500/40" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Examples Section
const ExamplesSection = ({ examples }) => {
  // Fallback data if API hasn't loaded yet
  const fallbackExamples = [
    { name: "Tim Howar", category: "Singer / West End", image_url: "https://images.unsplash.com/photo-1681149342107-b39e06d03f6b?w=600&q=80", website_url: "http://timhowar.com/" },
    { name: "Alice Fearn", category: "Singer / Actress", image_url: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&q=80", website_url: "http://www.alicefearn.com/" },
    { name: "Simon Lipkin", category: "Actor / Magician", image_url: "https://images.unsplash.com/photo-1743677042704-74a8390e765a?w=600&q=80", website_url: "#" },
    { name: "Come What May", category: "Tribute Show", image_url: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80", website_url: "https://comewhatmayshow.com/" },
    { name: "DJ Maxwell", category: "DJ / Producer", image_url: "https://images.unsplash.com/photo-1663668567002-6190b578c308?w=600&q=80", website_url: "#" },
    { name: "Sarah Grace", category: "Children's Entertainer", image_url: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=600&q=80", website_url: "#" },
  ];

  const displayExamples = examples.length > 0 ? examples : fallbackExamples;

  return (
    <section id="examples" className="py-28 md:py-36 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <span className="inline-block text-sm font-bold uppercase tracking-widest text-orange-500 mb-4 px-4 py-2 bg-orange-100 rounded-full">Portfolio</span>
          <h2 
            className="text-4xl md:text-6xl tracking-tight text-slate-900 mb-6"
            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
          >
            See what's possible
          </h2>
          <p className="text-xl text-slate-500">Real performers. Real websites. Real results.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayExamples.map((example, idx) => (
            <a 
              key={idx}
              href={example.website_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-200 hover:border-orange-200"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={example.image_url} 
                  alt={example.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-1">{example.name}</h3>
                <p className="text-slate-500 mb-4">{example.category}</p>
                <span className="inline-flex items-center text-orange-500 font-bold group-hover:gap-3 transition-all">
                  View Site <ArrowRight className="w-5 h-5 ml-2" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = ({ onGetStarted }) => {
  return (
    <section className="py-28 md:py-36 bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-black/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 text-center">
        <h2 
          className="text-5xl md:text-7xl tracking-tight text-white mb-8"
          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
        >
          Ready to look
          <br />
          like a pro?
        </h2>
        <p className="text-2xl text-white/80 mb-12 max-w-2xl mx-auto font-light">
          Join hundreds of performers who've upgraded their online presence.
        </p>
        <Button 
          onClick={onGetStarted}
          className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-12 py-8 text-xl font-black transition-all hover:scale-105 shadow-2xl"
          data-testid="cta-get-started-btn"
        >
          Get Your Boxit Site
          <ArrowRight className="w-6 h-6 ml-2" />
        </Button>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <BoxitLogo light size="large" />
            <p className="text-slate-400 mt-4 max-w-sm text-lg">
              AI-powered websites for performers. Your brand hub that stays fresh, looks incredible, and books you gigs.
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-wider text-sm">Quick Links</h4>
            <ul className="space-y-4 text-slate-400">
              <li><a href="#packages" className="hover:text-orange-400 transition-colors">Packages</a></li>
              <li><a href="#examples" className="hover:text-orange-400 transition-colors">Examples</a></li>
              <li><a href="#how-it-works" className="hover:text-orange-400 transition-colors">How It Works</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-6 uppercase tracking-wider text-sm">Get In Touch</h4>
            <ul className="space-y-4 text-slate-400">
              <li className="text-lg">hello@boxit.website</li>
              <li><a href="#" className="hover:text-orange-400 transition-colors">Live Chat</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Boxit. All rights reserved.</p>
          <div className="flex items-center gap-6 text-slate-500 text-sm">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
export default function LandingPage({ onLoginClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [siteContent, setSiteContent] = useState({
    examples: [],
    packages: [],
    stats: []
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch site content from API
  useEffect(() => {
    const fetchSiteContent = async () => {
      try {
        const [examplesRes, packagesRes, statsRes] = await Promise.all([
          publicSiteApi.getExamples(),
          publicSiteApi.getPackages(),
          publicSiteApi.getStats()
        ]);
        setSiteContent({
          examples: examplesRes.data.examples || [],
          packages: packagesRes.data.packages || [],
          stats: statsRes.data.stats || []
        });
      } catch (error) {
        console.error('Failed to load site content:', error);
        // Fallback data will be used by components
      }
    };
    fetchSiteContent();
  }, []);

  // Custom smooth scroll with slower, eased animation
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    
    const targetPosition = element.getBoundingClientRect().top + window.scrollY;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    const duration = 1200; // 1.2 seconds for smoother feel
    let startTime = null;

    // Ease-out cubic for smooth deceleration
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = easeOutCubic(progress);
      
      window.scrollTo(0, startPosition + distance * easeProgress);
      
      if (elapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  const handleGetStarted = () => {
    window.location.href = '/order';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation 
        scrolled={scrolled} 
        onLoginClick={onLoginClick}
        onGetStartedClick={handleGetStarted}
      />
      <HeroSection 
        onViewPackages={() => scrollToSection('packages')}
        onSeeExamples={() => scrollToSection('examples')}
      />
      <StatsSection stats={siteContent.stats} />
      <WhyWebsiteSection />
      <AIAdminSection />
      <PackagesSection />
      <HowItWorksSection />
      <ExamplesSection examples={siteContent.examples} />
      <FinalCTASection onGetStarted={handleGetStarted} />
      <Footer />
    </div>
  );
}
